import { useEffect, useState } from 'react'
import { NavLink, Outlet as ComponentOfCurrentRoute, useNavigate } from 'react-router'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import CustomerIcon from '@mui/icons-material/Business'
import LicenseIcon from '@mui/icons-material/VpnKey'
import BillingIcon from '@mui/icons-material/Payment'
import Person from '@mui/icons-material/Person'
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop'
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import HistoryIcon from '@mui/icons-material/History'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

import { SidebarLayout } from 'common-ui/components/SidebarLayout'
import { type SidebarMenuItemProps } from 'common-ui/components/SidebarMenuItem'
import { INITIAL_PATH, Path } from '../routes/path'
import { useAuth } from './login/Auth'
import { SidebarProvider, useSidebar } from './common/SidebarContext'
import netiLogoWhite from '../assets/netinsight-logo-color@3x.png'
import { BuildInfo, Role, UserManagementSystem } from 'common-billing-server/types'
import { isRoleAuthorized } from 'common-billing-server/role'
import { useBillingApi } from '../api/billing/billing-context'

interface MenuItem {
    name: string
    icon: React.ReactElement
    url?: Path
    children?: SidebarMenuItemProps[]
    minimumAllowedRole?: Role
    divider?: boolean
}

const allMenuItems: MenuItem[] = [
    { name: 'Customers', icon: <CustomerIcon />, url: Path.customers, minimumAllowedRole: Role.licenseAdmin },
    { name: 'Licenses', icon: <LicenseIcon />, url: Path.licenses, minimumAllowedRole: Role.licenseAdmin },
    { name: 'Installations', icon: <InstallDesktopIcon />, url: Path.installations },
    { name: 'Appliances', icon: <DnsOutlinedIcon />, url: Path.appliances },
    { name: 'Billing', icon: <BillingIcon />, url: Path.billing2 },
    { name: 'Billing (legacy)', icon: <BillingIcon />, url: Path.billing },
    {
        name: 'AI',
        icon: <AutoAwesomeIcon />,
        minimumAllowedRole: Role.super,
        children: [
            { name: 'Logs', url: Path.ai },
            { name: 'Settings', url: Path.aiSettings },
        ],
    },
    {
        name: 'Users',
        icon: <Person />,
        minimumAllowedRole: Role.licenseAdmin,
        divider: true,
        children: [
            { name: 'Internal', url: Path.internalUsers },
            { name: 'External', url: Path.externalUsers },
        ],
    },
    { name: 'Audit log', icon: <HistoryIcon />, url: Path.auditLog, minimumAllowedRole: Role.admin },
    { name: 'Global settings', icon: <SettingsIcon />, url: Path.settings, minimumAllowedRole: Role.super },
]

function filterMenuItems(role: Role): SidebarMenuItemProps[] {
    return allMenuItems
        .filter(({ minimumAllowedRole }) => !minimumAllowedRole || isRoleAuthorized({ role, minimumAllowedRole }))
        .map(({ name, icon, url, children, divider }) => ({ name, icon, url, children, divider }))
}

export const AppLayout = () => {
    return (
        <SidebarProvider>
            <AppLayoutContent />
        </SidebarProvider>
    )
}

const AppLayoutContent = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const api = useBillingApi()
    const { open, onOpen, onClose } = useSidebar()
    const [buildInfo, setBuildInfo] = useState<BuildInfo | undefined>(undefined)

    useEffect(() => {
        void api.getBuildInfo().then((info) => {
            setBuildInfo(info)
        })
    }, [api])

    const userRole = user?.role ?? Role.invoiceOnly
    const canManageSshKeys = isRoleAuthorized({ role: userRole, minimumAllowedRole: Role.admin })
    const profileUrl =
        user?.managedBy === UserManagementSystem.billingServer || canManageSshKeys ? Path.profile : undefined

    return (
        <Box sx={{ height: '100vh', width: '100vw', display: 'flex' }}>
            <SidebarLayout
                open={open}
                onOpen={onOpen}
                onClose={onClose}
                header={
                    <NavLink to={INITIAL_PATH} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <Box sx={{ mx: 2, my: 1 }}>
                            <img style={{ width: '100%' }} src={netiLogoWhite} alt="Net Insight logo" />

                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography variant="body2" align="center">
                                    Billing Manager
                                </Typography>
                            </Box>
                        </Box>
                    </NavLink>
                }
                menuItems={filterMenuItems(userRole)}
                user={{
                    name: user?.username ?? 'Unknown user',
                    profileUrl,
                    onLogout: () => navigate(Path.logout),
                }}
                version={
                    <Typography variant="caption" style={{ padding: '0 16px 8px' }}>
                        {buildInfo?.release || ''}
                    </Typography>
                }
            />
            <Box component="main" sx={{ flex: 1, overflowX: 'auto', overflowY: 'auto' }}>
                <ComponentOfCurrentRoute />
            </Box>
        </Box>
    )
}
